"use client";

import Link from "next/link";
import React from "react";
import { Logo } from "@/components/ui/logo";
import { Container } from "@/components/ui/container";
import { Phone, Smartphone, Mail } from "lucide-react";
import { SocialWidget } from "@/components/ui/social-widget";
import { ContactIcon } from "@/components/ui/contact-icon";
import { getSiteConfig } from "@/lib/config";

export function Footer() {
  const config = getSiteConfig();
  const year = config.company.copyrightYear ?? new Date().getFullYear();

  return (
    <footer className="w-full">
      {/* 1. Green Top Border */}
      <div className="h-[1px] w-full bg-secondary" />

      {/* 2. White Section with Links */}
      <div className="bg-white py-12 border-b border-gray-100">
        <Container>
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16">
            {/* Logo removed - moved to bottom bar */}

            {/* Navigation & Links */}
            <div className="flex flex-wrap md:flex-nowrap justify-center gap-y-2 gap-x-3 md:gap-x-6 text-black/70 font-medium tracking-wide text-xs md:text-[13px] lg:text-sm uppercase">
              {/* Service Links dynamically from config or filtered list */}
              {config.services.filter(s => s.enabled).slice(0, 4).map((service, idx) => (
                <React.Fragment key={service.slug}>
                  <Link href={service.href} className="hover:text-secondary transition-colors">
                    {service.title}
                  </Link>
                  {idx < config.services.filter(s => s.enabled).slice(0, 4).length - 1 && <span className="text-secondary hidden md:inline">|</span>}
                </React.Fragment>
              ))}


            </div>
          </div>
        </Container>
      </div>

      {/* 3. Purple Bottom Bar */}
      <div className="bg-[#391d5a] py-6 text-white text-sm">
        <Container>
          <div className="flex flex-col md:grid md:grid-cols-3 items-center gap-6 md:gap-4 text-center">

            {/* Left: Logo */}
            <div className="flex justify-center md:justify-start md:col-start-1">
              <div className="brightness-0 invert opacity-90">
                <Logo
                  variant="dark"
                  height={120}
                  width={360}
                  withBackground={false}
                  rounded={false}
                  containerClassName="w-auto"
                  className="!w-[230px] md:!w-[360px] h-auto"
                />
              </div>
            </div>

            {/* Center: Copyright */}
            <div className="text-white/80 text-xs md:text-sm md:col-start-2 md:justify-self-center whitespace-nowrap">
              © {year} {config.company.name}
            </div>

            {/* Right: Contact & Socials */}
            <div className="flex flex-col items-center md:items-end gap-4 md:col-start-3 md:justify-self-end">
              <div className="flex flex-wrap justify-center md:justify-end items-center gap-3">
                {/* Social Widget - expands to left */}
                <SocialWidget bgClassName="bg-[#391d5a]" />

                {/* Divider */}
                <div className="h-4 w-[1px] bg-white/20 mx-1" />

                {/* Contact Icons */}
                <div className="flex items-center gap-3">


                  {/* Email */}
                  <ContactIcon
                    href={`mailto:${config.company.email}`}
                    icon={Mail}
                    label={`Email: ${config.company.email}`}
                  />

                  {/* Office (Phone) - Text Link */}
                  {/* Office (Phone) */}
                  <a
                    href={`tel:${config.company.phone?.replace(/[^0-9]/g, "")}`}
                    className="flex items-center gap-2 group ml-1"
                  >
                    <div className="bg-secondary rounded-full p-2 group-hover:bg-[#7db336] transition-colors shadow-sm flex items-center justify-center">
                      <Phone className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white group-hover:text-secondary transition-colors font-medium">
                      {config.company.phone}
                    </span>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </Container>
      </div>
    </footer>
  );
}
